import React, { Fragment, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl/dist';

// Components

import AlertInfo from '../../../components/common/alert/AlertInfo';
import Form from '../../../lib/wrappers/Form';
import UniversalInput from '../../../lib/wrappers/UniversalInput';

// ActionCreators / Selectors

import { changeData } from '../../../actions/ssap/ssapActionCreators';
import { selectIfRealtimeCheckEnabled } from '../../../reducers/appReducer';

// Utils

import { cloneObject } from '../../../utils/miscellaneous/cloneObject';
import { getIfFtrQuestionOptional } from '../../../utils/householdMember/getIfFtrQuestionOptional';
import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { updateByPath } from '../../../utils/miscellaneous/updateByPath';

// Constants

import { configs } from '../../../constants/configs/configs';
import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { reconcileAptcTypes } from '../../../constants/ssap/hhmTypes/reconcileAptcTypes';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_62 = props => {

    const {
        data,
        data: {
            coverageYear
        },
        environment: {
            exchangeName,
            isState
        },
        taxHousehold,
        taxHousehold: {
            primaryTaxFilerPersonId
        }
    } = props;

    const {
        currentHhm,
        currentHhm: {
            name,
            personId
        },
        familyStatus: {
            currentHhm: currentHhmFamilyStatus
        },
        formName,
        hhmIndex: member,
        itfFeatureEnabled
    } = props;

    const isRealtimeCheckEnabled = useSelector(selectIfRealtimeCheckEnabled);

    const realtimeCheckBasedProps = useMemo(
        () => {
            const isQuestionOptional = getIfFtrQuestionOptional(isRealtimeCheckEnabled, currentHhmFamilyStatus);

            return {
                inputData: {
                    isInputRequired: !isQuestionOptional,
                    ...(
                        isQuestionOptional
                            ? { } :
                            { validationType: VALIDATION_TYPES.CHOSEN }
                    )
                },
                isAlertShown: isQuestionOptional,
                label: {
                    showRequiredIcon: !isQuestionOptional
                }
            };
        },
        [ isRealtimeCheckEnabled ]
    );

    const {
        externalEndpoints: {
            NV_LINKS: {
                RECONCILE_APTC
            }
        }
    } = configs;

    const isPTF = primaryTaxFilerPersonId === personId;

    const aptcProps = {
        previouslyReceivedAptc: {
            value: currentHhm.previouslyReceivedAptc,
            path: `taxHousehold.householdMember.${member}.previouslyReceivedAptc`
        },
        reconcileAptcOption: {
            value: itfFeatureEnabled && !isPTF
                ? currentHhm.reconcileAptcOption
                : taxHousehold.reconcileAptcOption,
            path: itfFeatureEnabled && !isPTF
                ? `taxHousehold.householdMember.${member}.reconcileAptcOption`
                : 'taxHousehold.reconcileAptcOption'
        },
        reconciledAptc: {
            path: itfFeatureEnabled && !isPTF
                ? `taxHousehold.householdMember.${member}.reconciledAptc`
                : 'taxHousehold.reconciledAptc'
        },
        failureToReconcile: {
            path: itfFeatureEnabled && !isPTF
                ? `taxHousehold.householdMember.${member}.failureToReconcile`
                : 'taxHousehold.failureToReconcile'
        }
    };

    const {
        previouslyReceivedAptc: {
            value: previouslyReceivedAptc,
            path: previouslyReceivedAptcPath
        },
        reconcileAptcOption: {
            value: reconcileAptcOption,
            path: reconcileAptcOptionPath
        },
        reconciledAptc: {
            path: reconciledAptcPath
        },
        failureToReconcile: {
            path: failureToReconcilePath
        }
    } = aptcProps;

    const onFieldChange_received_aptc = (...args) => {

        const [ , , , [ , value ] ] = args;

        let newState = { ...data };

        if(!value) {
            newState = updateByPath(newState, reconcileAptcOptionPath, reconcileAptcTypes.RECONCILIATION_NOT_ANSWERED);
            newState = updateByPath(newState, reconciledAptcPath, null);
            newState = updateByPath(newState, failureToReconcilePath, null);
        }

        newState = updateByPath(newState, previouslyReceivedAptcPath, value);

        props.actions.changeData(newState);
    };

    const onFieldChange = (...args) => {

        const [ , , , [ , value ] ] = args;
        
        let newState = { ...data };

        newState = updateByPath(newState, reconciledAptcPath, value === reconcileAptcTypes.RECONCILED || value === reconcileAptcTypes.NEVER_RECONCILED);
        newState = updateByPath(newState, reconcileAptcOptionPath, value);
        newState = updateByPath(newState, failureToReconcilePath, !newState.taxHousehold.reconciledAptc);
 
        props.actions.changeData(newState);
    };
    
    const getLink = () => {

        const linkAnchor = (
            <span className = 'font-weight-normal' >
                <FormattedMessage id = 'ssap.additional_info.reconcile_aptc.link_anchor' />
            </span>
        );

        switch(true) {
            case isState.NV:
                return (
                    <a
                        className = 'usa-external_link'
                        href = { RECONCILE_APTC }
                        target = '_blank'
                        rel = 'noopener noreferrer'
                    >
                        { linkAnchor }
                    </a>
                );

            default:
                return linkAnchor;
        }
    };

    return (
        <Fragment>
            <Form { ...{ formName } }>
                <div id = 'aiReconciledAptc' className = 'fade-in' >
                    <div className = 'subsection' >
                        {
                            (isState.PA || isState.NV) &&

                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.received_aptc',
                                    legendTextReplacements: {
                                        exchangeName
                                    }
                                } }
                                label = { {
                                    ulClass: [ 'margin-b-20' ],
                                    showRequiredIcon: true
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'previouslyReceivedAptc',
                                    value: previouslyReceivedAptc,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: currentHhm,
                                    fields: [],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange_received_aptc
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }

                        {
                            ((isState.PA && previouslyReceivedAptc) || (isState.NV && previouslyReceivedAptc) || (!isState.PA && !isState.NV) ) &&

                            <Fragment>
                                {
                                    realtimeCheckBasedProps.isAlertShown
                                        ? (
                                            <AlertInfo
                                                id = 'ssap.aptc_reconciliation.optional_question_alert'
                                                textReplacements = { {
                                                    exchangeName
                                                } }
                                                shouldParse
                                            />
                                        )
                                        : null
                                }

                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.aptc_reconcile',
                                        legendTextReplacements: {
                                            personName: (
                                                <strong>
                                                    { normalizeNameOfPerson(name) }
                                                </strong>
                                            ),
                                            link: getLink(),
                                            note: (
                                                <b>
                                                    <FormattedMessage id = 'ssap.input.legend.aptc_reconcile.note' />
                                                </b>
                                            )
                                        },
                                        dataInstructions: 'instructional_text.reconciliation_of_aptc',
                                        replacements: {
                                            exchangeName,
                                            previousYear: coverageYear - 1
                                        }
                                    } }
                                    label = { {
                                        ulClass: [ 'margin-b-20' ],
                                        ...realtimeCheckBasedProps.label
                                    } }
                                    inputData = { {
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'reconcileAptcOption',
                                        value: reconcileAptcOption,
                                        options: DROPDOWN_OPTIONS.RECONCILE_OPTIONS,
                                        optionsReplacements: { previousYear: coverageYear - 1 },
                                        valuesToMatch: reconcileAptcTypes.RECONCILIATION_NOT_ANSWERED,
                                        currentData: cloneObject(taxHousehold),
                                        fields: [],
                                        isInputStandalone: true,
                                        ...realtimeCheckBasedProps.inputData
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                            </Fragment>
                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,

    taxHousehold: state
        .ssap
        .data
        .taxHousehold
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_62);
